import _ from "lodash";

import { toast } from "react-toastify";
import { IField } from "../fields/types";
import { Handler } from "./../types/store-types";
import {
    AddFieldToPageActionType,
    ChangeCurrentPageActionType,
    ChangePageOrderFailureActionType,
    ChangePageOrderRequestActionType,
    ChangePageOrderSuccessActionType,
    CreatePageByTempIdActionType,
    CreatePageFailureActionType,
    CreatePageRequestActionType,
    CreatePageSuccessActionType,
    DeleteFieldActionType,
    DeletePageFailureActionType,
    DeletePageRequestActionType,
    DeletePageSuccessActionType,
    DuplicateFieldActionType,
    IChangePageDataFailure,
    IChangePageDataSuccess,
    InitPagesActionType,
    IPage,
    IPagesState,
    IRefreshPages,
    PagesActionTypes,
    RemoveActionType,
    ReplaceFieldIdActionType,
    UpdatePageDataRequestActionType,
    UpdatePageFailureActionType,
    UpdatePageRequestActionType,
    UpdatePageSuccessActionType,
} from "./types";

const initPages: Handler<IPagesState, InitPagesActionType> = (state, { payload: { list } }) => {
    return { ...state, list: [...list], current: list[0]._id };
};

const changeCurrentPage: Handler<IPagesState, ChangeCurrentPageActionType> = (state, { payload: { id } }) => {
    return { ...state, current: id };
};

/* -- Field Stuff --  */

const remove: Handler<IPagesState, RemoveActionType> = (state, { payload: { pageId } }) => {
    const { list } = state;
    return { ...state, list: list.filter((page: IPage) => page._id !== pageId) };
};

const addFieldToPage: Handler<IPagesState, AddFieldToPageActionType> = (state, { payload: { pageId, fieldId } }) => {
    const { list } = _.cloneDeep(state);
    const pageIndex = list.findIndex(page => page._id === pageId);
    if (pageIndex !== -1) {
        if (!list[pageIndex].fields.includes(fieldId)) {
            list[pageIndex].fields = [...list[pageIndex].fields, fieldId];
        }
    }
    return { ...state, list: [...list] };
};

const replaceFieldId: Handler<IPagesState, ReplaceFieldIdActionType> = (state, { payload: { tempId, fieldId } }) => {
    const { list } = state;
    list.forEach((value, key) => {
        value.fields.forEach((value_, key_) => {
            if (value_ === tempId) list[key].fields[key_] = fieldId;
        });
    });
    return { ...state, list };
};

const deleteField: Handler<IPagesState, DeleteFieldActionType> = (state, { payload: { pageId, fieldId } }) => {
    const list = _.cloneDeep(state.list);
    const currentPageIndex = list.findIndex(page => page._id === pageId);

    const newPage = list[currentPageIndex];
    newPage.fields = newPage.fields.filter(id => id !== fieldId);

    list[currentPageIndex] = newPage;

    return { ...state, list };
};

const duplicateField: Handler<IPagesState, DuplicateFieldActionType> = (
    state,
    { payload: { pageId, newFieldId, index: newFieldIndex } },
) => {
    if (!newFieldId) return state;

    const list = _.cloneDeep(state.list);

    const currentPageIndex = list.findIndex(page => page._id === pageId);

    const currentPageFields = _.cloneDeep(list[currentPageIndex].fields);

    const fieldsListFirstPart = _.slice(currentPageFields, 0, newFieldIndex);
    const fieldsListSecondPart = _.slice(currentPageFields, newFieldIndex);

    list[currentPageIndex].fields = _.concat(fieldsListFirstPart, newFieldId as IField["_id"], fieldsListSecondPart);

    return { ...state, list };
};

/* -- Create Page Handlers -- */

const createPageByTempId: Handler<IPagesState, CreatePageByTempIdActionType> = (state, { payload: { page } }) => {
    const { list } = state;
    return { ...state, list: [...list, _.cloneDeep(page)] };
};

const createPageRequest: Handler<IPagesState, CreatePageRequestActionType> = state => {
    return { ...state, isLoading: true };
};

const createPageSuccess: Handler<IPagesState, CreatePageSuccessActionType> = (state, { payload: { _id, tempId } }) => {
    const list = _.cloneDeep(state.list);
    list.forEach(item => {
        if (item._id === tempId) {
            item._id = _id;
            item.temp_id = tempId;
        }
    });

    return { ...state, list: [...list], isLoading: false, error: null };
};

const createPageFailure: Handler<IPagesState, CreatePageFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");
    return { ...state, error: payload, isLoading: false };
};

/* -- Delete Page Handlers -- */

const deletePageRequest: Handler<IPagesState, DeletePageRequestActionType> = (
    state,
    { payload: { id, removeFields } },
) => {
    const { list } = state;
    list.forEach((page, index) => {
        if (page._id === id) {
            if (removeFields) {
                list[index - 1].fields.push(...page.fields);
            }
            list.filter(page => page._id !== id);
        }
    });

    return { ...state, list: list.filter((page: IPage) => page._id !== id), isLoading: true };
};

const deletePageSuccess: Handler<IPagesState, DeletePageSuccessActionType> = (state, { payload: { id } }) => {
    return { ...state, error: null, isLoading: false };
};

const deletePageFailure: Handler<IPagesState, DeletePageFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");
    return { ...state, error: payload, isLoading: false };
};

/* -- Change Page Order Handlers -- */

const changePageOrderRequest: Handler<IPagesState, ChangePageOrderRequestActionType> = (
    state,
    { payload: { dragIndex, dropIndex } },
) => {
    const updatedList = state.list.map((page, index) => {
        if (index === dragIndex) return { ...state.list[dropIndex] };
        if (index === dropIndex) return { ...state.list[dragIndex] };

        return page;
    });

    return { ...state, list: updatedList, isLoading: true };
};

const changePageOrderSuccess: Handler<IPagesState, ChangePageOrderSuccessActionType> = state => {
    return { ...state, isLoading: false };
};

const changePageOrderFailure: Handler<IPagesState, ChangePageOrderFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");
    return { ...state, isLoading: false };
};

/* -- Update Page Order Handlers -- */

const updatePageRequest: Handler<IPagesState, UpdatePageRequestActionType> = (state, { payload: { _id, title } }) => {
    const newList = state.list.map(page => (_id === page._id ? { ...page, title } : page));

    return { ...state, list: newList, isLoading: true };
};

const updatePageFailure: Handler<IPagesState, UpdatePageFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");
    return { ...state, error: payload, isLoading: false };
};

const updatePageSuccess: Handler<IPagesState, UpdatePageSuccessActionType> = state => {
    return { ...state, isLoading: false };
};

export const updatePageDataRequest: Handler<IPagesState, UpdatePageDataRequestActionType> = (state, payload) => {
    return { ...state, list: payload.payload.data, isLoading: true };
};

export const updatePageDataFailure: Handler<IPagesState, IChangePageDataFailure> = (state, payload) => {
    return { ...state, error: payload };
};

export const updatePageDataSuccess: Handler<IPagesState, IChangePageDataSuccess> = (state, payload) => {
    return { ...state, isLoading: false };
};

export const refreshPages: Handler<IPagesState, IRefreshPages> = state => {
    const { list } = state;
    const newList = _.cloneDeep(list);

    return { ...state, list: newList };
};

export const handlersPAGES = {
    [PagesActionTypes.INIT_PAGES]: initPages,
    [PagesActionTypes.CHANGE_CURRENT_PAGE]: changeCurrentPage,
    [PagesActionTypes.REMOVE]: remove,
    [PagesActionTypes.ADD_FIELD_TO_PAGE]: addFieldToPage,
    [PagesActionTypes.REPLACE_FIELD_ID]: replaceFieldId,
    [PagesActionTypes.DELETE_FIELD]: deleteField,
    [PagesActionTypes.DUPLICATE_FIELD]: duplicateField,

    [PagesActionTypes.CREATE_PAGE_BY_TEMP_ID]: createPageByTempId,
    [PagesActionTypes.CREATE_PAGE_REQUEST]: createPageRequest,
    [PagesActionTypes.CREATE_PAGE_SUCCESS]: createPageSuccess,
    [PagesActionTypes.CREATE_PAGE_FAILURE]: createPageFailure,

    [PagesActionTypes.DELETE_PAGE_REQUEST]: deletePageRequest,
    [PagesActionTypes.DELETE_PAGE_SUCCESS]: deletePageSuccess,
    [PagesActionTypes.DELETE_PAGE_FAILURE]: deletePageFailure,

    [PagesActionTypes.CHANGE_PAGE_ORDER_REQUEST]: changePageOrderRequest,
    [PagesActionTypes.CHANGE_PAGE_ORDER_SUCCESS]: changePageOrderSuccess,
    [PagesActionTypes.CHANGE_PAGE_ORDER_FAILURE]: changePageOrderFailure,

    [PagesActionTypes.UPDATE_PAGE_DATA_REQUEST]: updatePageDataRequest,
    [PagesActionTypes.UPDATE_PAGE_DATA_FAILURE]: updatePageDataFailure,
    [PagesActionTypes.UPDATE_PAGE_DATA_SUCCESS]: updatePageDataSuccess,

    [PagesActionTypes.UPDATE_PAGE_REQUEST]: updatePageRequest,
    [PagesActionTypes.UPDATE_PAGE_FAILURE]: updatePageFailure,
    [PagesActionTypes.UPDATE_PAGE_SUCCESS]: updatePageSuccess,

    [PagesActionTypes.REFRESH_PAGES]: refreshPages,
};
